<template>
  <div class="contacts-list">
    <div class="filter-container">
      <ConditionGroupBuilder
        :value="filters"
        :manager="filtersManager"
        :disabled="reloading"
        @updated="onFiltersChanged"
      >
        <template #before>
          <ContactTypePicker
            class="contact-type-picker"
            :value="contactType"
            :disabled="reloading"
            @updated-type="contactTypeUpdated"
          ></ContactTypePicker>
        </template>
      </ConditionGroupBuilder>

      <ColumnPicker :columns="getColumns()" v-model="visibleColumns" />
    </div>

    <DataTable
      :data="formattedContacts"
      :columns="visibleColumns"
      :total="total"
      :loading="reloading"
      :pagination="true"
      :rows-per-page="rowsPerPage"
      default-sort-by="lastSeen"
      default-sort-direction="desc"
      @load="dataTableChanged"
      @cellClicked="handleCellClick"
    >
      <template v-slot:nodata>
        <div class="no-contacts">
          <PersonSearchIcon class="icon-2x"></PersonSearchIcon>
          <div class="no-contacts-message">
            <h1>No contacts match the filters</h1>
          </div>
        </div>
      </template>

      <template v-for="attribute in getContactAttributesToFormat()" v-slot:[attribute.id]="{ row }">
        <FriendlyString
          v-if="attribute.type !== 'date'"
          :value="row[attribute.id] ? row[attribute.id].toString() : undefined"
          :key="attribute.id"
        />
        <FriendlyDateTime
          v-if="attribute.type === 'date'"
          :date="row[attribute.id]"
          :placeholder="!attribute.system ? 'unknown' : 'never'"
          :key="attribute.id"
        />
      </template>
    </DataTable>
    <router-view></router-view>
  </div>
</template>

<script lang="ts" setup>
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import { contactsApi } from '@/features/contacts/contacts';
import PersonSearchIcon from '@/shared/components/PersonSearchIcon.vue';
import { ContactViewModel } from '@/features/contacts/models/contactViewModel';
import Toast from '@/shared/services/toast';
import ContactTypePicker from '@/features/contacts/components/ContactTypePicker.vue';
import { ContactType } from '@/features/contacts/models/contactType';
import DataTable from '@/shared/components/dataTable/DataTable.vue';
import ContactDisplayModel from '@/features/contacts/models/contactDisplayModel';
import { contactColumns } from '@/features/contacts/models/contactDataTableColumns';
import { ConditionGroupViewModel } from '@/shared/components/conditions/models/conditionGroupViewModel';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import ConditionGroupBuilder, { ConditionGroupUpdate } from '@/shared/components/conditions/ConditionGroupBuilder.vue';
import PageData from '@/shared/components/dataTable/pageData';
import FriendlyDateTime from '@/shared/components/FriendlyDateTime.vue';
import FriendlyString from '@/shared/components/FriendlyString.vue';
import { ContactAttributeViewModel } from '@/messaging/models/contactAttributeViewModel';
import AttributeIconUtil from '../../../shared/util/attributeIconUtil';
import ColumnPicker from '@/shared/components/dataTable/ColumnPicker.vue';
import DataTableColumn from '@/shared/components/dataTable/dataTableColumn';
import { useStore } from 'vuex';
import { computed, onBeforeMount, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useContactFilters } from '@/features/contacts/composables/useContactFilters';

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const contactAttributes = computed<ContactAttributeViewModel[]>(() => store.getters.contactAttributes);
const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);

const visibleColumns = ref<DataTableColumn[]>(contactColumns);

const contacts = ref<ContactViewModel[]>([]);
const formattedContacts = ref<ContactDisplayModel[]>([]);
const pageData = ref<PageData>();
const total = ref(0);
const rowsPerPage = ref(25);
const contactType = ref<ContactType>(ContactType.Lead);
const hasInvalidConditions = ref(false);
const previousFilterString = ref('');

const reloading = ref(false);
const filtersManager = useContactFilters();
const filters = ref<ConditionGroupViewModel>({
  type: 'and',
  conditions: [],
});

const router = useRouter();

const emit = defineEmits<{
  (e: 'loaded'): void;
}>();

onBeforeMount(async () => {
  if (!currentWorkspace.value) {
    handleSeriousError('No workspace selected');
    return;
  }
  await loadContacts();
  emit('loaded');
});

async function loadContacts() {
  try {
    reloading.value = true;
    const data = await contactsApi.getContacts(
      {
        offset: 0,
        limit: rowsPerPage.value,
        sort: {
          attribute: 'lastSeen',
          direction: 'desc',
        },
        filters: createFiltersForRequest(),
      },
      currentWorkspace.value.id,
    );
    total.value = data.total;
    contacts.value = data.page;
    formatData();
  } catch (err: any) {
    handleSeriousError(err.message);
    Toast.showError(`Error loading contacts ${err.message}`);
  } finally {
    reloading.value = false;
  }
}

function handleCellClick(contact: ContactViewModel) {
  router.push({
    name: 'contact-view',
    params: { id: contact.id },
  });
}

function getContactAttributesToFormat() {
  // all but name, as we want the default DataTable formatting so that the cell is clickable
  // also making a clone as changing the id of the reference means we'd have to strip the .customAttributes in a couple places
  const contactAttributesToFormat = JSON.parse(JSON.stringify(contactAttributes.value.slice(1)));
  for (const contactAttribute of contactAttributesToFormat) {
    if (!contactAttribute.system) {
      contactAttribute.id = getCustomAttributeId(contactAttribute.id);
    }
  }
  return contactAttributesToFormat;
}

function isNameEmpty(name: string) {
  return name.length === 0 || !name.trim();
}

function getColumns() {
  const allContactColumns: DataTableColumn[] = [];
  allContactColumns.push(...contactColumns);
  allContactColumns.push(...getCustomAttributeColumns());
  return allContactColumns;
}

function getContactName(name: string | undefined): string {
  if (name && !isNameEmpty(name)) {
    return name;
  }
  return 'Unknown';
}

async function onFiltersChanged(update: ConditionGroupUpdate) {
  filters.value = update.conditionGroup;
  hasInvalidConditions.value = update.invalid;

  const newFilterString = JSON.stringify(filters.value);
  if (previousFilterString.value !== newFilterString) {
    await reloadContacts(true);
    previousFilterString.value = newFilterString;
  }
}

async function contactTypeUpdated(newContactType: ContactType) {
  contactType.value = newContactType;
  await reloadContacts(true);
}

async function dataTableChanged(newPageData: PageData) {
  pageData.value = newPageData;
  await reloadContacts(false);
}

function getCustomAttributeColumns(): DataTableColumn[] {
  if (contactAttributes.value) {
    const customAttributes = contactAttributes.value.filter((a: ContactAttributeViewModel) => !a.system);
    return customAttributes.map((attribute: ContactAttributeViewModel) => {
      return {
        display: attribute.name,
        name: getCustomAttributeId(attribute.id),
        sortable: true,
        clickable: false,
        icon: AttributeIconUtil.getIconType(attribute.type, attribute.id, false),
      } as DataTableColumn;
    });
  }
  return [];
}

async function reloadContacts(clearOffset: boolean) {
  // don't automatically reload contacts list if there invalid conditions
  if (hasInvalidConditions.value) {
    return;
  }
  try {
    reloading.value = true;
    const data = await contactsApi.getContacts(
      {
        offset: !clearOffset && pageData.value ? pageData.value.offset : 0,
        limit: rowsPerPage.value,
        sort: {
          attribute: pageData.value ? pageData.value.sortBy : 'lastSeen',
          direction: pageData.value ? pageData.value.sortDirection : 'desc',
        },
        filters: createFiltersForRequest(),
      },
      currentWorkspace.value.id,
    );
    total.value = data.total;
    contacts.value = data.page;
    formatData();
  } catch (err: any) {
    handleSeriousError(err.message);
    Toast.showError(`Error loading contacts ${err.message}`);
  } finally {
    reloading.value = false;
  }
}

function createFiltersForRequest() {
  const typeFilter: ConditionViewModel = {
    attribute: 'type',
    operation: 'equals',
    type: 'string',
    value: contactType.value,
    result: true,
  };

  const req: ConditionGroupViewModel = {
    type: 'and',
    conditions: [typeFilter],
  };

  if (filters.value.conditions.length > 0) {
    req.conditions.push(filters.value);
  }

  return req;
}

function formatData() {
  const newFormattedContacts = [];
  for (const contact of contacts.value) {
    const formattedContact = {
      id: contact.id,
      name: getContactName(contact.name),
      email: contact.email,
      phoneNumber: contact.phoneNumber,
      lastSeen: contact.lastSeenUtc,
      lastContacted: contact.lastContactedUtc,
      lastHeardFrom: contact.lastHeardFromUtc,
      firstSeen: contact.firstSeenUtc,
    };
    addCustomAttributeValues(formattedContact, contact);

    newFormattedContacts.push(formattedContact);
  }
  formattedContacts.value = newFormattedContacts;
}

function addCustomAttributeValues(formattedContact: any, contact: ContactViewModel) {
  const customAttributes = contactAttributes.value.filter((a: ContactAttributeViewModel) => !a.system);
  for (const attribute of customAttributes) {
    formattedContact[getCustomAttributeId(attribute.id)] = contact.customAttributes[attribute.id];
  }
}

function getCustomAttributeId(id: string) {
  return `customAttributes.${id}`;
}
</script>

<style scoped lang="scss">
.contacts-list {
  position: relative;
}
.no-contacts {
  color: #212529;
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
  height: 65vh;

  .no-contacts-message {
    display: flex;
  }

  .material-design-icon {
    margin: 10px;
  }

  .icon-2x {
    height: 2em;
    width: 2em;
  }
}

.filter-container {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  margin-bottom: 1.5em;
}

:deep(.sk-folding-cube) {
  margin: 6rem auto;
}

.condition-group-builder {
  flex: 1;
}

.multiselect-container {
  flex: 0;
  align-self: flex-end;
}
</style>
